import type { BalanceSummary } from '@familywallet/shared';
import { useTranslation } from 'react-i18next';
import { useCurrentRates } from '../lib/ledger';
import { formatAmount, formatRateValue } from '../lib/money';

/**
 * The hero total card (docs/DESIGN.md §4): "Total you can see", the total in the base currency
 * (≈ when other currencies are converted at today's rate), the family rate pill, and one tile per
 * currency.
 */
export function HeroTotal({ summary, label }: { summary: BalanceSummary; label: string }) {
  const { t, i18n } = useTranslation();
  const rates = useCurrentRates();
  const rate = rates.data?.items.find((i) => i.current)?.current ?? null;
  const several = summary.totalByCurrency.length > 1 || summary.estimated;
  return (
    <section className="flex flex-col gap-4 rounded-3xl bg-hero p-5 text-hero-fg dark:border dark:border-line">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-hero-muted">{label}</span>
          {rate && (
            <span
              className="shrink-0 rounded-full border border-hero-muted/40 px-3 py-1 text-xs text-hero-muted tabular-nums"
              dir="ltr"
            >
              {t('rates.equation', {
                base: rate.baseCurrency,
                rate: formatRateValue(rate.rate, i18n.language),
                quote: rate.quoteCurrency,
              })}
            </span>
          )}
        </div>
        <span
          className="text-[38px] leading-tight font-semibold tracking-[-0.5px] whitespace-nowrap tabular-nums"
          dir="ltr"
        >
          {summary.estimated ? '≈ ' : ''}
          {formatAmount(summary.total, summary.currency, i18n.language)}
        </span>
      </div>
      {several && (
        <ul className="grid grid-cols-2 gap-2">
          {summary.totalByCurrency.map((c) => (
            <li
              key={c.currency}
              className="flex flex-col gap-0.5 rounded-2xl bg-white/8 px-3 py-2.5 dark:bg-surface-2"
            >
              <span className="text-xs text-hero-muted">{c.currency}</span>
              <span className="text-[15px] font-semibold tabular-nums" dir="ltr">
                {formatAmount(c.balance, c.currency, i18n.language)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {(summary.estimated || summary.missingRates.length > 0) && (
        <p className="text-xs text-hero-muted">
          {summary.estimated && t('rates.estimateNote')}{' '}
          {summary.missingRates.length > 0 &&
            t('rates.missingForEstimate', { currencies: summary.missingRates.join(', ') })}
        </p>
      )}
    </section>
  );
}
